import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AsyncPaginate } from "react-select-async-paginate";

// ------ CSS ------ //
import styles from "./KioskFullFlat.module.css";

// ------ Services ------ //
import * as badgeService from "../../../services/badgeManagementService";
import * as kioskService from "../../../services/kioskService";
import * as clientEventService from "../../../services/clientEventService";
import * as badgeManagementService from "../../../services/badgeManagementService";
import * as siteManagementService from "../../../services/siteManagementService";

// ------ Components ------ //
import EditField from "../../../components/Kiosk/KioskEdit/EditField"
import LoadingSpinner from "../../../components/Miscellaneous/LoadingSpinner";

const fetchAttendeeData = async (reqBody) => {
    const item = await kioskService.getSmallAttendeesWithGuestsSearchPaged(reqBody);
    return item || {};
};

const defaultImage =
    "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fGV2ZW50JTIwd2FsbHBhcGVyfGVufDB8fDB8fHww";

const KioskFullFlat = ({ user }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // General state
    const [isLoading, setIsLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const [prevEntry, setPrevEntry] = useState(null);

    // Attendee/Registrant state
    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [registrant, setRegistrant] = useState(location.state ? location.state : {});
    const [registrants, setRegistrants] = useState([]);
    const [stylingForm, setStylingForm] = useState({});

    // Search state
    const [itemsPerPage] = useState(15);
    const [client, setClient] = useState({});
    const [clientEventInfo, setClientEventInfo] = useState({});
    // Kiosk & badge settings
    const [kioskSetting, setKioskSetting] = useState(null);
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [headerImage, setHeaderImage] = useState(null);
    const [kioskImage, setKioskImage] = useState(null);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [layoutOptions, setLayoutOptions] = useState([]);
    const [templateSetting, setTemplateSetting] = useState(null);
    const [templateString, setTemplateString] = useState(null);
    const [htmlString, setHTMLString] = useState(null);
    const [kioskImageStyle, setKioskImageStyle] = useState(null);

    // Row state for table rendering
    const [a1, setA1] = useState();
    const [a2, setA2] = useState();
    const [a3, setA3] = useState();
    const [a1toA2, setA1toA2] = useState();
    const [a2toA3, setA2toA3] = useState();
    const [a1toA3, setA1toA3] = useState();
    const [b1, setB1] = useState();
    const [b2, setB2] = useState();
    const [b3, setB3] = useState();
    const [b1toB2, setB1toB2] = useState();
    const [b2toB3, setB2toB3] = useState();
    const [b1toB3, setB1toB3] = useState();
    const [c1, setC1] = useState();
    const [c2, setC2] = useState();
    const [c3, setC3] = useState();
    const [c1toC2, setC1toC2] = useState();
    const [c2toC3, setC2toC3] = useState();
    const [c1toC3, setC1toC3] = useState();
    const [d1, setD1] = useState();
    const [d2, setD2] = useState();
    const [d3, setD3] = useState();
    const [d1toD2, setD1toD2] = useState();
    const [d2toD3, setD2toD3] = useState();
    const [d1toD3, setD1toD3] = useState();


    // Popup/Reset state for print complete
    const [showPrintPopup, setShowPrintPopup] = useState(false);
    const [countdown, setCountdown] = useState(10);
    const countdownTimer = useRef();
    const [isAutoReset, setIsAutoReset] = useState(false);

    // Print � opens the full badge HTML document in a new window and prints it.
    // react-to-print cannot print an iframe's srcDoc content (it clones the
    // outer iframe DOM node, not the document inside it). Using window.open()
    // is the correct approach when the print target is a complete HTML string.
    const handlePrint = () => {
        if (!htmlString) return
        const printWindow = window.open('', '_blank')
        printWindow.document.open()
        printWindow.document.write(htmlString)
        printWindow.document.close()
        // Allow the badge template's window.onload script to run and
        // populate the badges-container before triggering the print dialog.
        setTimeout(() => {
            printWindow.focus()
            printWindow.print()
            printWindow.close()
        }, 500);
        let registrantsHolder = [];
        registrants.forEach((registrant) => {
            // update each record for storage
            //const registrantHolder = JSON.parse(registrant);
            registrant.ClientName = clientEventInfo.clientName;
            registrantsHolder.push(registrant);
        })
        let printedRegistrants = {
            clientId: clientEventInfo.clientId || user.ClientId,
            eventId: clientEventInfo.eventId || user.EventId,
            printedRecords: registrantsHolder
        }
        kioskService.recordAttendeesPrint(printedRegistrants);
        // Show popup with countdown after print
        setShowPrintPopup(true);
        setCountdown(10);
        setIsAutoReset(true);
        // clear form 
        //clearFormData()
        //setDefaults();
    }

    // Smooth reset after slide transition
    const handleReset = () => {
        setCurrentStep(0); // Slide to Section 1 first
        setShowPrintPopup(false);
        setCountdown(10);
        setIsAutoReset(false);
        clearInterval(countdownTimer.current);
        setTimeout(() => {
            setSelectedAttendee(null);
            setRegistrant({});
            setRegistrants([]);
            setStylingForm({});
            setIsConfirmed(false);
            // Optionally: clear badgeDimensions, printSettings, etc if needed here
        }, 250); // Match slide animation duration
    };

    useEffect(() => {
        if (showPrintPopup && isAutoReset) {
            countdownTimer.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev === 1) {
                        clearInterval(countdownTimer.current);
                        handleReset();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(countdownTimer.current);
    }, [showPrintPopup, isAutoReset]);

    // Utility for px
    const appendPx = (value) =>
        value && !value.toString().includes("px") ? `${value}px` : value;

    const createRequestBody = async () => {

        let requestBody = {}

        if (clientEventInfo.clientId && clientEventInfo.eventId) {
            requestBody =
            {
                "clientId": `${clientEventInfo.clientId}`,
                "eventId": `${clientEventInfo.eventId}`
            }
        }

        return await requestBody;
    }

    // Data init
    useEffect(() => {
        if (!clientEventInfo?.clientId || !clientEventInfo?.clientName) return;

        const fetchData = async () => {
            try {
                const req = await createRequestBody();
                const templateSettings = await badgeService.retrieveFlatTemplate(req);
                if (templateSettings) {
                    setTemplateSetting(templateSettings);
                    setTemplateString(templateSettings.templateString)
                }
                else {
                    setLoaded(true);
                }
            } catch (error) {
                console.error("Error fetching template settings or field ordering:", error);
            }
        };

        fetchData();
    }, [clientEventInfo]);

    useEffect(() => {
        const localclientevent = clientEventService.getClientEvent();
        if (localclientevent) {
            setClientEventInfo(localclientevent);
            setClient(localclientevent);
        } else if (user.ClientId && user.EventId) {
            setClient({ clientId: user.ClientId, eventId: user.EventId });
        }

        const getCountries = async () => {
            let countrieslist = await siteManagementService.getCountries();
            if (countrieslist && countrieslist.length > 0) {
                let USA = countrieslist.find((item) => item.countryKey === "US");
                let OtherCountries = countrieslist.filter((item) => item.countryKey !== "US");
                if (USA) OtherCountries.unshift(USA);
                setCountries(OtherCountries);
            } else {
                setCountries(countrieslist);
            }
        };

        const getStates = async () => {
            let stateList = await siteManagementService.getStates();
            setStates(stateList);
        };

        getCountries();
        getStates();
    }, [user.ClientId, user.EventId]);

    useEffect(() => {
        if (!client?.clientId) return;

        const fetchData = async () => {
            const req = {
                clientId: `${client.clientId}`,
                eventId: `${client.eventId}`,
            };
            const kioskSettings = await kioskService.getSetting(req);
            const layoutSettings = await badgeManagementService.getKioskEditScreenSetupWithLayout(req);
            if (kioskSettings) {
                let imageHolder = '';
                if (kioskSettings.useBackgroundImage && kioskSettings.backgroundBase64Image) {
                    setBackgroundImage({
                        base64Image: kioskSettings.backgroundBase64Image,
                        backgroundString: `url(${kioskSettings.backgroundBase64Image})`,
                    });
                    imageHolder = `${kioskSettings.backgroundBase64Image}`;
                    let holder = {
                        //base64Image: kioskSettings.backgroundBase64Image,
                        backgroundImage: `url('${imageHolder}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundAttachment: 'fixed',
                        backgroundRepeat: 'no-repeat',
                    }
                    setKioskImageStyle(holder);
                } else if (kioskSettings.headerBase64Image) {
                    setHeaderImage({
                        base64Image: kioskSettings.headerBase64Image,
                        width: appendPx(kioskSettings.headerImageWidth),
                        height: appendPx(kioskSettings.headerImageHeight),
                    });
                    imageHolder = `${kioskSettings.headerBase64Image}`;
                    let holder = {
                        // "url('" + imageHolder + "')",
                        //base64Image: kioskSettings.headerBase64Image,
                        backgroundImage: `url('${imageHolder}')`,
                        backgroundSize: `${appendPx(kioskSettings.headerImageWidth)} ${appendPx(kioskSettings.headerImageHeight)}`,
                        backgroundPosition: '0px 0px',
                        //backgroundAttachment: 'fixed',
                        backgroundRepeat: 'no-repeat',
                    }
                    setKioskImageStyle(holder);
                    const rootElement = document.getElementById("root");
                    if (rootElement) {
                        rootElement.style.width = `${appendPx(kioskSettings.headerImageWidth)}`;
                        rootElement.style.backgroundColor = `#fff`;
                        rootElement.style.minHeight = `100%`;
                        rootElement.style.position = `relative`;
                        rootElement.style.marginTop = `0px`;
                        rootElement.style.marginRight = `auto`;
                        rootElement.style.marginLeft = `auto`;
                    }
                    
                }
                setKioskSetting(kioskSettings);
                // no image
                if (!imageHolder || imageHolder.length <1) {
                    setLoaded(true);
                }
            }
            else {
                setLoaded(true);
                return;
            }
            if (Array.isArray(layoutSettings)) setLayoutOptions(layoutSettings);

            // Pre-fill layout field values from registrant
            if (Array.isArray(layoutSettings) && registrant) {
                layoutSettings.forEach((field) => {
                    let regVal =
                        registrant[
                        Object.keys(registrant).find(
                            (key) => key.toLowerCase() === field.cssFieldName?.toLowerCase()
                        )
                        ];
                    if (field.cssFieldName && regVal) {
                        field.fieldValue = regVal;
                    }
                });
            }
        };

        fetchData();
    }, [client, registrant]);

    useEffect(() => {
        if (kioskSetting) {
            const img = backgroundImage ? backgroundImage : headerImage ? headerImage : defaultImage;

            setKioskImage(img);
            if (img) setLoaded(true);
        }

    }, [backgroundImage, headerImage]);

    useEffect(() => {
        // assign fields for each row for use in rowTables config
        if (layoutOptions && layoutOptions.length) {
            setA1(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'a1'));
            setA2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'a2'));
            setA3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'a3'));
            setA1toA2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'a1toa2'));
            setA2toA3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'a2toa3'));
            setA1toA3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'a1toa3'));

            setB1(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'b1'));
            setB2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'b2'));
            setB3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'b3'));
            setB1toB2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'b1tob2'));
            setB2toB3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'b2tob3'));
            setB1toB3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'b1tob3'));

            setC1(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'c1'));
            setC2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'c2'));
            setC3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'c3'));
            setC1toC2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'c1toc2'));
            setC2toC3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'c2toc3'));
            setC1toC3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'c1toc3'));

            setD1(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'd1'));
            setD2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'd2'));
            setD3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'd3'));
            setD1toD2(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'd1tod2'));
            setD2toD3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'd2tod3'));
            setD1toD3(layoutOptions.find(key => key.kioskLayoutOptionName?.toLowerCase() === 'd1tod3'));
        }
    }, [layoutOptions]);

    // Table configs for wide/wider/widest per row (Section 2)
    // Only assign width class if field exists; no empty <td>s!
    const rowTables = [
        {
            tableStyle: styles.table_A,
            cells: [
                { field: a1toA3, width: styles.widest },
                { field: a1toA2, width: styles.wider },
                { field: a1, width: styles.wide },
                { field: a2toA3, width: styles.wider },
                { field: a2, width: styles.wide },
                { field: a3, width: styles.wide }
            ]
        },
        {
            tableStyle: styles.table_B,
            cells: [
                { field: b1toB3, width: styles.widest },
                { field: b1toB2, width: styles.wider },
                { field: b1, width: styles.wide },
                { field: b2toB3, width: styles.wider },
                { field: b2, width: styles.wide },
                { field: b3, width: styles.wide }
            ]
        },
        {
            tableStyle: styles.table_C,
            cells: [
                { field: c1toC3, width: styles.widest },
                { field: c1toC2, width: styles.wider },
                { field: c1, width: styles.wide },
                { field: c2toC3, width: styles.wider },
                { field: c2, width: styles.wide },
                { field: c3, width: styles.wide }
            ]
        },
        {
            tableStyle: styles.table_D,
            cells: [
                { field: d1toD3, width: styles.widest },
                { field: d1toD2, width: styles.wider },
                { field: d1, width: styles.wide },
                { field: d2toD3, width: styles.wider },
                { field: d2, width: styles.wide },
                { field: d3, width: styles.wide }
            ]
        }
    ];

    const renderRowTable = ({ tableStyle, cells }) => (
        <table className={tableStyle}>
            <tbody>
                <tr className={styles.fillTable}>
                    {cells.map(({ field, width }, i) =>
                        field && (
                            <td key={field.kioskLayoutOptionName || i} className={width}>
                                {field.fieldLabel}
                                <br />
                                <EditField
                                    attendeeData={stylingForm}
                                    option={field}
                                    states={states}
                                    countries={countries}
                                    handleChange={handleChange}
                                />
                            </td>
                        )
                    )}
                </tr>
            </tbody>
        </table>
    );

    // Helper: get real key name from object (case-insensitive)
    const getPropName = (obj, propname) => {
        if (!obj) return propname;
        for (let key in obj) {
            if (key.toLowerCase() === propname.toLowerCase()) return key;
        }
        return propname;
    };

    // Handle edit field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        // Always use camelCase for field keys
        const fieldKey = name.charAt(0).toLowerCase() + name.replace(/\s+/g, "").slice(1);

        const combos = {};
        const updatedForm = { ...stylingForm };

        // Combo logic for names and combos
        if (["firstname", "lastname"].includes(fieldKey)) {
            const firstName = fieldKey === "firstname" ? value : stylingForm.firstname || "";
            const lastName = fieldKey === "lastname" ? value : stylingForm.lastname || "";

            if (stylingForm.lastNameFirstName_Combo)
                combos.lastNameFirstName_Combo = `${lastName}, ${firstName}`;
            if (stylingForm.fullName_Combo) combos.fullName_Combo = `${firstName} ${lastName}`;
            if (stylingForm.fullNameCredentials_Combo)
                combos.fullNameCredentials_Combo = `${firstName}${lastName}, ${stylingForm.credentials || ""}`;
            if (stylingForm.fullNameTitle_Combo)
                combos.fullNameTitle_Combo = `${firstName}${lastName}, ${stylingForm.title || ""}`;
            if (stylingForm.fullNameDesignation_Combo)
                combos.fullNameDesignation_Combo = `${firstName}${lastName}, ${stylingForm.designation || ""}`;
        }

        if (fieldKey === "city" && stylingForm.cityState_Combo)
            combos.cityState_Combo = `${value}, ${stylingForm.state || ""}`;
        if (fieldKey === "state" && stylingForm.cityState_Combo)
            combos.cityState_Combo = `${stylingForm.city || ""}, ${value}`;

        if (["credentials", "title", "designation"].includes(fieldKey)) {
            const keyMap = {
                credentials: "fullNameCredentials_Combo",
                title: "fullNameTitle_Combo",
                designation: "fullNameDesignation_Combo",
            };
            const comboKey = keyMap[fieldKey];
            if (stylingForm[comboKey])
                combos[comboKey] = `${stylingForm.firstname || ""}${stylingForm.lastname || ""}, ${value}`;
        }

        // Update actual field (case-insensitive)
        updatedForm[fieldKey] = value;

        setStylingForm(updatedForm);
    };

    // On Confirm: merge stylingForm back into registrant and registrants
    const handleConfirmEdit = () => {
        // Create a merged registrant using normalized keys
        const merged = { ...registrant };
        for (const key in stylingForm) {
            const realKey = getPropName(merged, key);
            merged[realKey] = stylingForm[key];
        }
        setRegistrant(merged);
        setRegistrants([merged]);
        setIsConfirmed(true);
        setCurrentStep(2);
    };

    const GeneratePreview = () => {
        if (templateString !== undefined && stylingForm !== undefined) {
            let jsonObject = '';
            if (Array.isArray(stylingForm)) {
                let arrayItemsHolder = [];
                stylingForm.forEach((item) => {
                    const regValItem = {};
                    for (const key in item) {
                        regValItem[key.charAt(0).toUpperCase() + key.slice(1)] = item[key];
                    }
                    arrayItemsHolder.push(regValItem); 
                });
                jsonObject = JSON.stringify(arrayItemsHolder);
            }
            else {
                
                const normalized = {};
                for (const key in stylingForm) {
                    normalized[key.charAt(0).toUpperCase() + key.slice(1)] = stylingForm[key];
                }
                let arrayHolder = [normalized];
                jsonObject = JSON.stringify(arrayHolder);
            }
            if (jsonObject && jsonObject !== '[]') {

                let newHTML = JSON.parse(templateString).replace("\"%ATTENDEEINFO%\"", JSON.stringify(jsonObject));
                //console.log(newHTML);
                setHTMLString(newHTML);
            }

        }
    }
    useEffect(() => {
        if (templateString && stylingForm) {
            GeneratePreview();
        }

    }, [stylingForm]);
    // Navigation (now waits for transition before hiding Section 3)
    const scrollToPreviousStep = () => {
        setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
        setTimeout(() => {
            setIsConfirmed(false);
        }, 250); // match your CSS transition
    };
    const handleFocus = (event) => {
        // triggered when selected
        if (!menuOpen) {
            setMenuOpen(true);
        }
    };
    // Handle unfocus event
    const handleBlur = (event) => {
        if (menuOpen) {
            setMenuOpen(false);
        }
    };
    // Handle input change event
    const handleInputChange = (event) => {
        if (menuOpen) {
            setMenuOpen(false);
        }
    };
    useEffect(() => {
        if (selectedAttendee) {
            if (menuOpen) {
                setMenuOpen(false);
            }
        }
        else {
            setMenuOpen(true);
        }
    }, [selectedAttendee]);
    // AsyncPaginate: paginated search for attendees
    const loadOptions = async (inputValue, loadedOptions, { page }) => {
        const reqBody = {
            clientId: clientEventInfo.clientId || user.ClientId,
            eventId: clientEventInfo.eventId || user.EventId,
            searchValue: inputValue,
            index: page || 1,
            pageSize: itemsPerPage,
        };
        const itemsString = await kioskService.getSmallAttendeesWithGuestsSearchPaged(reqBody);
        const items = JSON.parse(itemsString)
        items.forEach((item) => {
            item.value = item.LocalRegistrationId;
            item.label =
                item.FirstName +
                " " +
                item.LastName +
                (item.Organization ? " (" + item.Organization + ")" : "");
        });
        if (items && items.length === 1) {
            // if there's only one option, auto-select
            setSelectedAttendee(items[0]);
            setMenuOpen(false);
        }
        return {
            options: items,
            hasMore: items.length === itemsPerPage,
            additional: {
                page: (page || 1) + 1,
            },
        };
    };
    // Handle attendee select (shows Continue in Section 1)
    // hits here after clearing entries
    const goToEditSection = () => setCurrentStep(1);
    const handleSelectAttendee = async (selectedOption) => {
        if (!selectedOption) {
            setSelectedAttendee(null);
            return;
        }
        setSelectedAttendee(selectedOption);
        setMenuOpen(false);
        setIsLoading(true);
        const reqBody = {
            clientId: clientEventInfo.clientId || user.ClientId,
            eventId: clientEventInfo.eventId || user.EventId,
            searchValue: selectedOption.LocalRegistrationId,
        };
        const fetchedDataString = await fetchAttendeeData(reqBody);
        const fetchedData = JSON.parse(fetchedDataString)
        const regObj = Array.isArray(fetchedData) ? fetchedData[0] : fetchedData;
        setRegistrant(regObj);
        // Normalize keys for stylingForm
        const normalized = {};
        for (const key in regObj) {
            normalized[key.charAt(0).toLowerCase() + key.slice(1)] = regObj[key];
        }
        setStylingForm(normalized);

        // Parse badge template data if available
        if (regObj && regObj.mainTemplate) {
            const template = regObj.mainTemplate;
            let savedStylingForm = template?.stylingForm ? JSON.parse(template.stylingForm) : {};
            // Normalize loaded stylingForm keys as well
            if (savedStylingForm && Object.keys(savedStylingForm).length) {
                const normStylingForm = {};
                for (const key in savedStylingForm) {
                    normStylingForm[key.charAt(0).toLowerCase() + key.slice(1)] = savedStylingForm[key];
                }
                setStylingForm(normStylingForm);
            } else {
                setStylingForm(normalized);
            }
        }

        setIsConfirmed(false);
        setIsLoading(false);
        // Do NOT advance section yet ("Continue" button will do this)
    };

    return (
        <>
            {loaded ? kioskSetting ? 
                <>
                    
                    <div
                        className={styles.container}
                        style={kioskImageStyle && kioskImageStyle.backgroundImage && kioskImageStyle.backgroundSize ?
                            {
                                backgroundImage: `${kioskImageStyle.backgroundImage}`,
                                backgroundSize: `${kioskImageStyle.backgroundSize}`,
                                backgroundPosition: `${kioskImageStyle.backgroundPosition}`,
                                backgroundAttachment: `${kioskImageStyle.backgroundAttachment}`,
                                backgroundRepeat: 'no-repeat',
                            }
                            : {}}
                    >
                        <div
                            className={styles.sectionsWrapper}
                            style={{ transform: `translateY(-${currentStep * 100}vh)` }}
                        >
                            {/* Step 1: Search Attendee */}
                            <div className={styles.sectionBG}>
                                <div className={styles.sectionContainer}>
                                    {kioskSetting.hideEventTitle === false && kioskSetting.eventTitle ?
                                        <h2 className={styles.sectionHeading}>
                                            {kioskSetting.eventTitle}
                                        </h2>
                                        : <></>}
                                    <AsyncPaginate
                                        value={selectedAttendee}
                                        loadOptions={loadOptions}
                                        isClearable={true}
                                        isSearchable={true}
                                        onChange={handleSelectAttendee}
                                        debounceTimeout={300}
                                        additional={{ page: 1 }}
                                        placeholder={kioskSetting.searchLabel}
                                        id={styles.searchBar}
                                        menuIsOpen={menuOpen}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        onInputChange={handleInputChange}
                                    />
                                    {/* Show Continue button if attendee is selected */}
                                    {selectedAttendee && (
                                        <button
                                            className={styles.button}
                                            type="button"
                                            onClick={goToEditSection}
                                            style={{ marginTop: 24 }}
                                        >
                                            Continue
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Step 2: Edit Attendee */}
                            {selectedAttendee && (
                                <div className={styles.sectionBG}>
                                    <div className={styles.sectionContainer}>
                                        {kioskSetting.hideEventKioskInstruction === false && kioskSetting.eventKioskInstruction ?
                                            <h2 className={styles.sectionHeading}>{kioskSetting.eventKioskInstruction}</h2>
                                            : <></>}
                                        {isLoading ? (
                                            <p>Loading...</p>
                                        ) : (
                                            <>
                                                {rowTables.map(row => renderRowTable(row))}
                                                <div className={styles.buttonSection}>
                                                    <button
                                                        className={styles.backButton}
                                                        type="button"
                                                        onClick={scrollToPreviousStep}
                                                    >
                                                        Back
                                                    </button>
                                                    <button
                                                        className={styles.button}
                                                        type="button"
                                                        onClick={handleConfirmEdit}
                                                    >
                                                        Confirm & Continue
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Print Preview */}
                            {isConfirmed && (
                                <div className={styles.sectionBG}>
                                    {templateString && htmlString && (
                                        <div className={styles.sectionContainerPrintPage}>
                                            <div className={styles.buttonSection}>
                                                <button
                                                    className={styles.backButton}
                                                    type="button"
                                                    onClick={scrollToPreviousStep}
                                                >
                                                    Back
                                                </button>
                                                <button
                                                    className={styles.button}
                                                    type="button"
                                                    onClick={handlePrint}
                                                >
                                                    Print Badge
                                                </button>
                                                <button
                                                    className={styles.resetButton}
                                                    type="button"
                                                    onClick={handleReset}
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Print complete popup modal */}
                        {showPrintPopup && (
                            <div className={styles.printPopupOverlay}>
                                <div className={styles.printPopup}>
                                    <p>
                                        Returning to attendee search in {countdown} second{countdown !== 1 ? "s" : ""}...<br />
                                        <strong>Want to stay here?</strong>
                                    </p>
                                    <button className={styles.button} onClick={() => {
                                        clearInterval(countdownTimer.current);
                                        handleReset();
                                    }}>
                                        Print New Attendee
                                    </button>
                                    <button className={styles.resetButton} onClick={() => {
                                        setIsAutoReset(false);
                                        clearInterval(countdownTimer.current);
                                        setShowPrintPopup(false);
                                    }}>
                                        Stay
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                </>
                
                : <div> Kiosk not configured!</div>
                
                 : 
                <LoadingSpinner />
            }
        </>
    );
};

export default KioskFullFlat;